import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router";
import moment from "moment";

import { AppointmentJoin } from "@/repositories/appointments.repository";
import { appointment_status, AppointmentMetaData } from "@/models/appointment.model";
import { cancel, goBack, reschedule } from "@/app/pmc/appointments/appointments.slice";
import { useAppointments } from "@/app/pmc/appointments/appointments.hooks";
import { canRescheduleOrCancel, canGoback } from "@/lib/utils/appointment-time-validation";
import { calendarDay, isReschedulableDay } from "@/lib/appointment-reschedule";
import { firstGoBackDay, isGoBackDay } from "@/lib/appointment-go-back";
import { fieldActionMessage, isConflictRefusal } from "@/lib/appointment-field-actions";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Eye, CalendarClock, XCircle, RotateCcw } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

type NextActionType = "reschedule" | "goback" | "cancel";

interface NextAction {
  type: NextActionType;
  next: appointment_status;
}

const labelFor: Record<NextAction["type"], string> = {
  reschedule: "Reschedule Appointment",
  goback: "Go Back",
  cancel: "Cancel Appointment",
};

interface ActionWithValidation {
  action: NextAction;
  disabled?: boolean;
  reason?: string;
}

export default function ActionsDropdown({ row, onRefetch }: { row: AppointmentJoin; onRefetch?: () => unknown }) {
  const { dispatch, refetch } = useAppointments();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAct, setSelectedAct] = useState<NextAction | null>(null);
  const [newDate, setNewDate] = useState<Date | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const getAvailableActions = (): ActionWithValidation[] => {
    const actions: ActionWithValidation[] = [];
    const meta = (row.meta_data as AppointmentMetaData) || {};

    switch (row.status) {
      case appointment_status.pending:
      case appointment_status.assigned:
      case appointment_status.accepted: {
        const timeCheck = canRescheduleOrCancel(row.date, row.time);
        if (timeCheck.can) {
          actions.push({ action: { type: "reschedule", next: appointment_status.pending } });
          actions.push({ action: { type: "cancel", next: appointment_status.cancelled } });
        } else {
          actions.push({
            action: { type: "reschedule", next: appointment_status.pending },
            disabled: true,
            reason: timeCheck.reason,
          });
          actions.push({
            action: { type: "cancel", next: appointment_status.cancelled },
            disabled: true,
            reason: timeCheck.reason,
          });
        }
        break;
      }

      case appointment_status.inprogress:
        // No actions - dropdown will be disabled
        break;

      case appointment_status.rescheduled: {
        const timeCheck = canRescheduleOrCancel(row.date, row.time);
        if (timeCheck.can) {
          actions.push({ action: { type: "cancel", next: appointment_status.cancelled } });
        } else {
          actions.push({
            action: { type: "cancel", next: appointment_status.cancelled },
            disabled: true,
            reason: timeCheck.reason,
          });
        }
        break;
      }

      case appointment_status.done:
      case appointment_status.completed: {
        const gobackCheck = canGoback(meta.done_at);
        if (gobackCheck.can) {
          actions.push({ action: { type: "goback", next: appointment_status.goback } });
        } else {
          actions.push({
            action: { type: "goback", next: appointment_status.goback },
            disabled: true,
            reason: gobackCheck.reason,
          });
        }
        break;
      }

    }

    return actions;
  };

  const availableActions = getAvailableActions();
  const isDropdownDisabled =
    row.status === appointment_status.inprogress ||
    row.status === appointment_status.cancelled;
  
  const showViewDetails = 
    row.status === appointment_status.pending ||
    row.status === appointment_status.assigned ||
    row.status === appointment_status.accepted ||
    row.status === appointment_status.done ||
    row.status === appointment_status.completed ||
    row.status === appointment_status.rescheduled;

  const startAction = (act: NextAction) => {
    setTimeout(() => {
      setSelectedAct(act);
      setNewDate(act.type === "goback" ? firstGoBackDay() : undefined);
      setDialogOpen(true);
    }, 1);
  };

  const doChange = async () => {
    if (!selectedAct || submitting) return;

    setSubmitting(true);
    try {
      if (selectedAct.type === "cancel") {
        await dispatch(cancel({ id: row.id })).unwrap();
      } else if (selectedAct.type === "reschedule") {
        await dispatch(reschedule({ id: row.id, date: calendarDay(newDate!) })).unwrap();
      } else {
        await dispatch(goBack({ id: row.id, date: calendarDay(newDate!) })).unwrap();
      }

      toast.success(`${labelFor[selectedAct.type]} successful`);
      setDialogOpen(false);
    } catch (error) {
      toast.error(fieldActionMessage(error, "Status update failed"));
      if (isConflictRefusal(error)) {
        setDialogOpen(false);
        (onRefetch ?? refetch)();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getIconForAction = (actionType: NextActionType) => {
    switch (actionType) {
      case "reschedule":
        return <CalendarClock className="w-4 h-4 mr-2" />;
      case "cancel":
        return <XCircle className="w-4 h-4 mr-2" />;
      case "goback":
        return <RotateCcw className="w-4 h-4 mr-2" />;
      default:
        return null;
    }
  };

  const renderMenuItem = (actionWithValidation: ActionWithValidation) => {
    const { action, disabled, reason } = actionWithValidation;
    const icon = getIconForAction(action.type);
    const menuItem = (
      <DropdownMenuItem
        key={action.type}
        onClick={() => !disabled && startAction(action)}
        disabled={disabled}
        variant={action.type === "cancel" ? "destructive" : "default"}
        className={disabled ? "opacity-50 cursor-not-allowed" : ""}
      >
        {icon}
        {labelFor[action.type]}
      </DropdownMenuItem>
    );

    if (disabled && reason) {
      return (
        <Tooltip key={action.type}>
          <TooltipTrigger asChild>
            <span className="block cursor-not-allowed" title={reason}>
              {menuItem}
            </span>
          </TooltipTrigger>
          <TooltipContent>{reason}</TooltipContent>
        </Tooltip>
      );
    }

    return menuItem;
  };

  const triggerButton = (
    <DropdownMenuTrigger asChild>
      <Button size="icon" variant="ghost" aria-label="Open menu" disabled={isDropdownDisabled}>
        <MoreHorizontal className="w-4 h-4" />
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <>
      <DropdownMenu>
        {isDropdownDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
            <TooltipContent>
              {row.status === appointment_status.inprogress
                ? "No actions available for appointments in progress"
                : "No actions available for cancelled appointments"}
            </TooltipContent>
          </Tooltip>
        ) : (
          triggerButton
        )}

        <DropdownMenuContent align="end" className="w-56">
          {showViewDetails && (
            <>
              <DropdownMenuItem asChild>
                <Link to={`/appointments/${row.id}`}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Link>
              </DropdownMenuItem>
              {availableActions.length > 0 && <DropdownMenuSeparator />}
            </>
          )}
          {availableActions.map((actionWithValidation) => renderMenuItem(actionWithValidation))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedAct ? labelFor[selectedAct.type] : ""}</DialogTitle>
            {selectedAct?.type === "cancel" && (
              <DialogDescription>
                This action cannot be undone. The appointment will be marked&nbsp;
                <strong>cancelled</strong>.
              </DialogDescription>
            )}
          </DialogHeader>

          {(selectedAct?.type === "reschedule" || selectedAct?.type === "goback") && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Pick the new date for this appointment.</p>
              <Calendar
                mode="single"
                selected={newDate}
                defaultMonth={newDate ?? (row.date ? moment(row.date).toDate() : undefined)}
                onSelect={setNewDate}
                disabled={selectedAct?.type === "reschedule" ? (day) => !isReschedulableDay(day, row.date) : (day) => !isGoBackDay(day)}
                className="rounded-md border"
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={doChange}
              disabled={
                submitting ||
                !selectedAct ||
                ((selectedAct?.type === "reschedule" || selectedAct?.type === "goback") && !newDate)
              }
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
